import { useEffect, useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { viewFor } from "@/poker/engine";
import { fmt } from "@/poker/cards";

const PRE = [
  { key: "checkfold", label: "Check / Fold" },
  { key: "check", label: "Check" },
  { key: "callany", label: "Call Any" },
];

const PreActions = ({ preAction, setPreAction }) => (
  <div data-testid="pre-action-bar" className="flex flex-wrap gap-2 justify-center sm:justify-end">
    {PRE.map((o) => (
      <label key={o.key} data-testid={`pre-action-${o.key}`} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs sm:text-sm font-semibold transition-colors ${preAction === o.key ? "border-amber-400 bg-amber-400/10 text-amber-300" : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}>
        <input type="checkbox" className="accent-amber-400" checked={preAction === o.key} onChange={() => setPreAction(preAction === o.key ? null : o.key)} />
        {o.label}
      </label>
    ))}
  </div>
);

const ActBtn = ({ testId, onClick, cls, children, hint }) => (
  <button data-testid={testId} onClick={onClick} className={`relative flex-1 min-w-0 h-12 sm:h-14 rounded-xl font-heading text-base sm:text-xl uppercase tracking-wide font-bold shadow-lg transition-[background-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 active:translate-y-0 ${cls}`}>
    {children}
    {hint && <kbd className="hidden lg:inline absolute top-1 right-2 text-[10px] font-mono opacity-60">{hint}</kbd>}
  </button>
);

export const ActionBar = ({ game, onAct, preAction, setPreAction }) => {
  const me = game.players[0];
  const myTurn = game.phase === "betting" && game.toAct === 0;
  const v = useMemo(() => (myTurn ? viewFor(game, 0) : null), [game, myTurn]);
  const [amount, setAmount] = useState(0);

  useEffect(() => { if (v) setAmount(v.minRaiseTo); }, [v]);

  useEffect(() => {
    if (!v) return undefined;
    const onKey = (e) => {
      if (e.target.tagName === "INPUT") return;
      const k = e.key.toLowerCase();
      if (k === "f" && v.toCall > 0) onAct("fold");
      if (k === "c") onAct(v.toCall ? "call" : "check");
      if (k === "r" && v.canRaise) onAct("raise", amount);
      if (k === "a" && v.canRaise) onAct("raise", v.maxTo);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [v, amount, onAct]);

  if (me.out) return null;
  if (!myTurn) {
    const inHand = !me.folded && !me.allIn && game.phase === "betting";
    return (
      <div data-testid="action-bar-waiting" className="action-shell flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="text-sm text-slate-400">
          {me.folded ? "You folded — watching the hand" : me.allIn ? "You are all-in" : game.phase === "betting" ? `Waiting for ${game.players[game.toAct]?.name}…` : "Dealing…"}
        </span>
        {inHand && <PreActions preAction={preAction} setPreAction={setPreAction} />}
      </div>
    );
  }

  const potAfterCall = v.pot + v.toCall;
  const clamp = (x) => Math.max(v.minRaiseTo, Math.min(v.maxTo, Math.round(x)));
  const preset = (f) => setAmount(clamp(v.currentBet + f * potAfterCall));
  const raiseLabel = v.currentBet === 0 ? "Bet" : "Raise to";
  const isAllIn = amount >= v.maxTo;
  const callAllIn = v.toCall >= v.stack;

  return (
    <div data-testid="action-bar" className="action-shell space-y-3">
      {v.canRaise && (
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="flex gap-1.5 flex-wrap">
            {[["min", "Min", () => setAmount(v.minRaiseTo)], ["half-pot", "½ Pot", () => preset(0.5)], ["three-quarter-pot", "¾ Pot", () => preset(0.75)], ["pot", "Pot", () => preset(1)], ["all-in", "All-in", () => setAmount(v.maxTo)]].map(([id, label, fn]) => (
              <button key={id} data-testid={`preset-btn-${id}`} onClick={fn} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs sm:text-sm font-semibold text-slate-200 hover:bg-amber-500/20 hover:border-amber-500/50 transition-colors">
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-1">
            <Slider data-testid="bet-amount-slider" className="flex-1" min={v.minRaiseTo} max={v.maxTo} step={Math.max(1, game.sb)} value={[amount]} onValueChange={([x]) => setAmount(x)} />
            <input
              data-testid="bet-amount-input" type="number" value={amount} min={v.minRaiseTo} max={v.maxTo}
              onChange={(e) => setAmount(Number(e.target.value))} onBlur={() => setAmount(clamp(amount))}
              className="w-24 sm:w-28 h-9 rounded-lg bg-black/50 border border-white/15 px-2 font-mono text-sm text-amber-300 focus:outline-none focus:border-amber-400"
            />
          </div>
        </div>
      )}
      <div className="flex gap-2 sm:gap-3">
        {v.toCall > 0 && <ActBtn testId="action-btn-fold" hint="F" onClick={() => onAct("fold")} cls="bg-red-600 hover:bg-red-500 text-white">Fold</ActBtn>}
        <ActBtn testId="action-btn-check-call" hint="C" onClick={() => onAct(v.toCall ? "call" : "check")} cls="bg-emerald-600 hover:bg-emerald-500 text-white">
          {v.toCall ? <>{callAllIn ? "Call All-in" : "Call"} <span className="font-mono text-sm sm:text-base">{fmt(v.toCall)}</span></> : "Check"}
        </ActBtn>
        {v.canRaise && (
          <ActBtn testId="action-btn-bet-raise" hint="R" onClick={() => onAct("raise", clamp(amount))} cls="bg-amber-500 hover:bg-amber-400 text-black">
            {isAllIn ? "All-in" : raiseLabel} <span className="font-mono text-sm sm:text-base">{fmt(clamp(amount))}</span>
          </ActBtn>
        )}
      </div>
    </div>
  );
};
